import { useEffect } from 'react';
import { useAppDispatch } from './useAppDispatch';
import * as userSlice from '../data/user.slice';
import authService from '../services/authService';

/**
 * Reacts to logins completed in the login popup (see
 * docs/popup-login-plan.md).
 *
 * Origin validation, payload validation and applying the session to this
 * window all happen inside authService, which owns the single `message`
 * listener. By the time this hook is notified, `authService.isLoggedIn()` is
 * already current — which matters because it is read synchronously during
 * render and is not reactive state. Dispatching here is what actually triggers
 * the re-render.
 *
 * Call once, at app startup.
 */
export const useCognitoPopupLogin = (): void => {
  const dispatch = useAppDispatch();

  useEffect(
    () =>
      authService.onPopupLoginSuccess(() => {
        // The app is already mounted with `loggedInUserInitialized === true`
        // from the pre-login fetch, so AppRouting would skip its loading state
        // and evaluate routes against the still-empty user — which bounces to
        // /noaccess before the fresh user info lands. Reset the flag so it
        // waits, the same sequence a cold start goes through. The redirect flow
        // gets this for free from handleLoginCallback's full page reload.
        dispatch(userSlice.setLoggedInUserInitialized(false));
        dispatch(userSlice.fetchLoggedInUserInfoAsync());
      }),
    [dispatch],
  );
};

export default useCognitoPopupLogin;
